import { z } from 'zod';
/**
 * Versión vigente: textos oficiales Autoevaluación v1.0 + Framework v1.4.
 * El parser mantiene compatibilidad con v1/v2/v3 (respondidas sobre textos provisionales).
 */
export const SCHEMA_VERSION = 4;
/**
 * Primera versión respondida sobre los textos oficiales: los agregados y el benchmark
 * solo comparan submissions con schema_version >= este valor (ítems < v4 medían otra cosa).
 */
export const SCHEMA_VERSION_MIN_AGREGADO = 4;
export function esComparableParaAgregado(schemaVersion) {
    return schemaVersion >= SCHEMA_VERSION_MIN_AGREGADO;
}
export const DIMENSIONES = [
    'EV',
    'PC',
    'OD',
    'DC',
    'GS',
    'IN',
];
export const DIMENSION_LABELS = {
    EV: 'Estrategia y Valor',
    PC: 'Personas y Cultura',
    OD: 'Operación y Delivery',
    DC: 'Datos y Conocimiento',
    GS: 'Gobernanza, Seguridad y Cumplimiento',
    IN: 'Innovación con IA',
};
/** Dimensiones que entran al nivel_general (MIN); IN es índice aparte. */
export const DIMENSIONES_NIVEL = ['EV', 'PC', 'OD', 'DC', 'GS'];
export const opcionLetraSchema = z.enum(['a', 'b', 'c', 'd', 'na', 'ns', 'nc']);
/** a→1 … d→4; respuesta especial unificada (`na`, y legacy `ns`/`nc`) → 0. */
export function letraAPuntos(letra) {
    const map = {
        a: 1,
        b: 2,
        c: 3,
        d: 4,
        na: 0,
        ns: 0,
        nc: 0,
    };
    return map[letra];
}
/**
 * UI unifica No aplica / No sabe / No contesta en una sola opción (`na`).
 * `ns`/`nc` se siguen aceptando al leer borradores y submissions viejas.
 */
export function letraParaUi(letra) {
    if (letra === 'ns' || letra === 'nc')
        return 'na';
    return letra;
}
export function esRespuestaEspecial(letra) {
    return letra === 'na' || letra === 'ns' || letra === 'nc';
}
export const puntuableSchema = z.object({
    EV1: opcionLetraSchema,
    EV2: opcionLetraSchema,
    EV3: opcionLetraSchema,
    EV4: opcionLetraSchema,
    /** v1.0 · 1.5 Economía de la IA (opcional en drafts legacy schemaVersion≤2). */
    EV5: opcionLetraSchema.optional(),
    PC1: opcionLetraSchema,
    PC2: opcionLetraSchema,
    PC3: opcionLetraSchema,
    PC4: opcionLetraSchema,
    OD1: opcionLetraSchema,
    OD2: opcionLetraSchema,
    OD3: opcionLetraSchema,
    OD4: opcionLetraSchema,
    DC1: opcionLetraSchema,
    DC2: opcionLetraSchema,
    DC3: opcionLetraSchema,
    DC4: opcionLetraSchema,
    GS1: opcionLetraSchema,
    GS2: opcionLetraSchema,
    GS3: opcionLetraSchema,
    GS4: opcionLetraSchema,
    IN1: opcionLetraSchema,
    IN2: opcionLetraSchema,
    IN3: opcionLetraSchema,
    IN4: opcionLetraSchema,
});
export const perfilOrgSchema = z.object({
    p0_1: z.string().min(1),
    p0_2: z.string().min(1),
    p0_3: z.string().min(1),
    p0_4: z.string().min(1),
    p0_5: z.string().min(1),
    p0_6: z.string().min(1),
    p0_7: z.string().min(1),
    p0_8: z.string().min(1),
    /** v1.0 · antigüedad (opcional hasta completar catálogo en wizard). */
    p0_9: z.string().min(1).optional(),
    /** v1.0 · provincia sede (catálogo a validar con la cámara). */
    p0_10: z.string().min(1).optional(),
});
/** Prefijo fijo del campo sitio web en el wizard. */
export const WEBSITE_HTTPS_PREFIX = 'https://';
/** Quita http(s):// para mostrar el dominio al lado del prefijo visual. */
export function websiteSinProtocolo(value) {
    return value.trim().replace(/^https?:\/\//i, '');
}
/**
 * Garantiza esquema https://. Acepta `www.empresa.com` sin protocolo
 * (causa frecuente de rechazo silencioso del type=url / Zod `.url()`).
 */
export function websiteConHttps(value) {
    const bare = websiteSinProtocolo(value);
    if (!bare)
        return '';
    return `${WEBSITE_HTTPS_PREFIX}${bare}`;
}
const websiteUrlSchema = z
    .string()
    .trim()
    .min(1, 'Ingrese el sitio web de la organización')
    .max(300)
    .transform(websiteConHttps)
    .pipe(z.string().url('Ingrese un sitio web válido (ej. www.empresa.com)'));
export const registroSchema = z.object({
    nombre: z.string().trim().min(2).max(120),
    email: z.string().trim().email().max(200),
    organizacion: z.string().trim().min(2).max(200),
    website: websiteUrlSchema,
    linkedin: z
        .string()
        .trim()
        .max(300)
        .optional()
        .or(z.literal(''))
        .transform((v) => (v === '' ? undefined : v)),
    consentimiento: z.literal(true),
});
export const submissionInputSchema = z
    .object({
    schemaVersion: z
        .union([z.literal(1), z.literal(2), z.literal(3), z.literal(SCHEMA_VERSION)])
        .default(SCHEMA_VERSION),
    honeypot: z.string().max(0).optional(),
})
    .merge(registroSchema)
    .merge(perfilOrgSchema)
    .merge(puntuableSchema);
/**
 * Columnas export app (extiende §14.2 hacia contrato Tally v1.0).
 * Incluye version/consentimiento/p0_9/p0_10/EV5; mantiene orden legible para mesa.
 */
export const CSV_COLUMNS = [
    'nombre',
    'email',
    'organizacion',
    'website',
    'linkedin',
    'consentimiento',
    'submitted_at',
    'version',
    'p0_1',
    'p0_2',
    'p0_3',
    'p0_4',
    'p0_5',
    'p0_6',
    'p0_7',
    'p0_8',
    'p0_9',
    'p0_10',
    'EV1',
    'EV2',
    'EV3',
    'EV4',
    'EV5',
    'PC1',
    'PC2',
    'PC3',
    'PC4',
    'OD1',
    'OD2',
    'OD3',
    'OD4',
    'DC1',
    'DC2',
    'DC3',
    'DC4',
    'GS1',
    'GS2',
    'GS3',
    'GS4',
    'IN1',
    'IN2',
    'IN3',
    'IN4',
];
export const fichaCodigoSchema = z.enum([
    '1.1',
    '1.2',
    '1.3',
    '1.4',
    '1.5',
    '2.1',
    '2.2',
    '2.3',
    '2.4',
    '3.1',
    '3.2',
    '3.3',
    '3.4',
    '4.1',
    '4.2',
    '4.3',
    '4.4',
    'G.1',
    'G.2',
    'G.3',
    'G.4',
]);
export const arquetipoSchema = z.enum([
    'Adopción por delante del control',
    'Discurso adelantado',
    'Práctica silenciosa',
    'Perfil parejo',
    'Perfil desbalanceado',
]);
export const segmentoSchema = z.enum([
    'Factory exportadora',
    'PyME de servicios local',
    'Producto en escala',
    'Corporación regional',
    'Consultora en transformación',
    'Nacida con IA',
    'Sin segmento',
]);
export const scoreResultSchema = z.object({
    prom_EV: z.number(),
    prom_PC: z.number(),
    prom_OD: z.number(),
    prom_DC: z.number(),
    prom_GS: z.number(),
    prom_IN: z.number(),
    nivel_EV: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    nivel_PC: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    nivel_OD: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    nivel_DC: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    nivel_GS: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    indice_IN: z.number(),
    nivel_general: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    dimension_foco: z.enum(['EV', 'PC', 'OD', 'DC', 'GS']),
    balance: z.number(),
    brecha_ee: z.number(),
    brecha_g: z.number(),
    arquetipo: arquetipoSchema,
    segmento: segmentoSchema,
    dimension_arrastra: z.string(),
    fichas: z.tuple([fichaCodigoSchema, fichaCodigoSchema, fichaCodigoSchema]),
    fichas_niveles: z.tuple([
        z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
        z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
        z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    ]),
});
export const perfilEmailSchema = z.object({
    resumen: z.string().min(1).max(2000),
    recomendaciones: z.array(z.string().min(1).max(500)).min(2).max(3),
});
export function normalizarOrganizacion(nombre) {
    return nombre
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .replace(/\b(s\.?\s*a\.?|s\.?\s*r\.?\s*l\.?|s\.?\s*a\.?\s*s\.?)\b/gi, '')
        .replace(/[.,]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/** Dominios de email personal (§4.3) — lista configurable. */
export const PERSONAL_EMAIL_DOMAINS = [
    'gmail.com',
    'googlemail.com',
    'hotmail.com',
    'hotmail.es',
    'outlook.com',
    'outlook.es',
    'live.com',
    'yahoo.com',
    'yahoo.com.ar',
    'icloud.com',
    'me.com',
    'proton.me',
    'protonmail.com',
    'aol.com',
    'mail.com',
    'gmx.com',
    'gmx.net',
];
export function isPersonalEmailDomain(email) {
    const domain = email.trim().toLowerCase().split('@')[1] ?? '';
    return PERSONAL_EMAIL_DOMAINS.includes(domain);
}
export function emailDomain(email) {
    return email.trim().toLowerCase().split('@')[1] ?? '';
}
/** Normaliza dominio web: sin protocolo, www, path ni trailing slash. */
export function normalizarWebsiteDomain(websiteOrDomain) {
    let s = websiteOrDomain.trim().toLowerCase();
    s = s.replace(/^https?:\/\//, '');
    s = s.replace(/^www\./, '');
    s = s.split('/')[0] ?? s;
    s = s.split('?')[0] ?? s;
    return s.replace(/\.$/, '');
}
// --- OTP (§4.3 / §11) ---
export const otpChannelSchema = z.enum(['email', 'sms']);
/**
 * Normaliza teléfono a E.164. Default país AR (+54).
 * Acepta +54…, 54…, 0…, 15… (móviles AR comunes).
 */
export function normalizarTelefonoE164(raw, defaultCountry = 'AR') {
    const digits = raw.replace(/[^\d+]/g, '').trim();
    if (!digits)
        return null;
    let n = digits;
    if (n.startsWith('+'))
        n = n.slice(1);
    n = n.replace(/\D/g, '');
    if (defaultCountry === 'AR') {
        // 54911… / 5411… ya con país
        if (n.startsWith('54') && n.length >= 12 && n.length <= 13) {
            return `+${n}`;
        }
        // 15XXXXXXXX → móvil sin área (asumimos 11)
        if (n.startsWith('15') && n.length === 10) {
            return `+54911${n.slice(2)}`;
        }
        // 01115… / 011…
        if (n.startsWith('0'))
            n = n.slice(1);
        // 11 + 8 dígitos (fijo/móvil BA) o 9xx…
        if (n.length === 10 && n.startsWith('11')) {
            // Móvil AR en E.164 suele ser +54 9 11 …
            return `+549${n}`;
        }
        if (n.length === 10) {
            return `+54${n}`;
        }
        if (n.length === 11 && n.startsWith('9')) {
            return `+54${n}`;
        }
    }
    if (n.length >= 10 && n.length <= 15)
        return `+${n}`;
    return null;
}
const otpRequestBaseSchema = z.object({
    email: z.string().trim().email().max(200),
    nombre: z.string().trim().min(1).max(120).optional(),
    consentimiento: z.literal(true).optional(),
    channel: otpChannelSchema.default('email'),
    phone: z.string().trim().max(24).optional(),
});
export const otpRequestSchema = otpRequestBaseSchema
    .superRefine((v, ctx) => {
    if (v.channel !== 'sms')
        return;
    if (!v.consentimiento) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'consentimiento requerido para SMS',
            path: ['consentimiento'],
        });
    }
    if (!v.phone?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'teléfono requerido para SMS',
            path: ['phone'],
        });
        return;
    }
    if (!normalizarTelefonoE164(v.phone)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'teléfono inválido (use formato internacional, ej. +54911…)',
            path: ['phone'],
        });
    }
})
    .transform((v) => ({
    ...v,
    phone: v.channel === 'sms' && v.phone ? normalizarTelefonoE164(v.phone) : undefined,
}));
export const otpVerifySchema = z.object({
    email: z.string().trim().email().max(200),
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'código de 6 dígitos'),
});
export const otpVerifyResponseSchema = z.object({
    otp_token: z.string().min(22),
    personal_email: z.boolean(),
});
// --- Organization suggest (§7 / §11) ---
export const organizationSuggestQuerySchema = z.object({
    domain: z.string().trim().min(1).max(253),
});
export const organizationSuggestItemSchema = z.object({
    id: z.string().uuid(),
    canonical_name: z.string(),
    website_domain: z.string().nullable(),
    linkedin_url: z.string().nullable(),
    verified: z.boolean(),
});
export const organizationSuggestResponseSchema = z.object({
    items: z.array(organizationSuggestItemSchema),
});
// --- Responses draft / patch (§11) ---
export const QUESTION_CODES = [
    'EV1', 'EV2', 'EV3', 'EV4', 'EV5',
    'PC1', 'PC2', 'PC3', 'PC4',
    'OD1', 'OD2', 'OD3', 'OD4',
    'DC1', 'DC2', 'DC3', 'DC4',
    'GS1', 'GS2', 'GS3', 'GS4',
    'IN1', 'IN2', 'IN3', 'IN4',
];
export const createResponseSchema = z.object({
    otp_token: z.string().min(22).max(200),
    identity: z.object({
        nombre: z.string().trim().min(2).max(120),
    }),
    consent: z.literal(true),
    organization: z.object({
        name: z.string().trim().min(2).max(200),
        website: websiteUrlSchema,
        linkedin: z
            .string()
            .trim()
            .max(300)
            .optional()
            .or(z.literal(''))
            .transform((v) => (v === '' ? undefined : v)),
    }),
});
export const draftAnswersSchema = z.record(z.string().regex(/^(EV[1-5]|PC[1-4]|OD[1-4]|DC[1-4]|GS[1-4]|IN[1-4])$/), opcionLetraSchema);
export const draftPatchSchema = z.object({
    answers: draftAnswersSchema.optional(),
    profile: perfilOrgSchema.partial().optional(),
});
export const createResponseResponseSchema = z.object({
    draft_token: z.string().min(22),
});
export const submitResponseResponseSchema = z.object({
    results_token: z.string().min(22),
    score: z.object({
        nivel_general: scoreResultSchema.shape.nivel_general,
        indice_IN: z.number(),
        dimension_foco: scoreResultSchema.shape.dimension_foco,
        niveles: z.object({
            EV: scoreResultSchema.shape.nivel_EV,
            PC: scoreResultSchema.shape.nivel_PC,
            OD: scoreResultSchema.shape.nivel_OD,
            DC: scoreResultSchema.shape.nivel_DC,
            GS: scoreResultSchema.shape.nivel_GS,
        }),
    }),
    replaced_canonical: z.boolean().optional(),
});
export const resumeRequestSchema = z.object({
    email: z.string().trim().email().max(200),
});
// --- Results + benchmark (§5 / §11) ---
export const benchmarkPayloadSchema = z.object({
    enabled: z.boolean(),
    n: z.number().int().nonnegative(),
    message: z.string().optional(),
    promedioPorDimension: z
        .object({
        EV: z.number(),
        PC: z.number(),
        OD: z.number(),
        DC: z.number(),
        GS: z.number(),
    })
        .nullable(),
    indiceInnovacionPromedio: z.number().nullable(),
    cut: z.string().nullable().optional(),
    cutCounts: z.record(z.string(), z.number()).nullable().optional(),
});
export const resultsPayloadSchema = z.object({
    results_token: z.string(),
    organization: z.object({
        name: z.string(),
        website: z.string().nullable().optional(),
    }),
    score: scoreResultSchema,
    recomendaciones: z.array(z.string()).min(2).max(3),
    /** URLs a fichas del sitio HTML del framework (latest). */
    fichas_links: z
        .array(z.object({
        codigo: fichaCodigoSchema,
        nombre: z.string(),
        url: z.string().url().or(z.string().min(1)),
        nivel: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    }))
        .min(2)
        .max(3)
        .optional(),
    recomendacion_url: z.string().optional(),
    benchmark: benchmarkPayloadSchema.nullable(),
    personal_email: z.boolean(),
    submitted_at: z.string(),
});
/** Umbrales normativos §5.4 (N general bajado de 10 a 5 por decisión del dueño 2026-10-06; k ≥ 5 por grupo se mantiene). */
export const BENCHMARK_MIN_N = 5;
export const BENCHMARK_MIN_K = 5;
/** Cuántas organizaciones canónicas faltan para abrir el benchmark general. */
export function organizacionesFaltantesBenchmark(n, minN = BENCHMARK_MIN_N) {
    return Math.max(0, minN - Math.max(0, n));
}
//# sourceMappingURL=index.js.map